import { Box, Chip, Tooltip } from '@mui/material';
import { Layers } from '@mui/icons-material';
import { MRT_ColumnDef } from 'material-react-table';
import { Util } from '../../util/Util';
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from '../../util/constants';
import type { ExpenseRow } from './expenseTypes';
import { expenseCategoryLabel, expenseNumber } from './expenseTypes';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const expenseColumns = (
  t: Translate,
  options?: {
    onRefClick?: (row: ExpenseRow) => void;
    onBatchClick?: (row: ExpenseRow) => void;
    batchCounts?: Map<string, number>;
  },
): MRT_ColumnDef<ExpenseRow>[] => [
  {
    header: t('common:tables.date'),
    accessorKey: 'date',
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      return value ? Util.formatDate(value, 'medium') : '—';
    },
  },
  {
    header: t('common:expense.number'),
    accessorKey: 'number',
    accessorFn: (row) => expenseNumber(row),
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
    Cell: ({ row }) => {
      const count = options?.batchCounts?.get(String(row.original.batch || '')) || 0;
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
            onClick={() => options?.onRefClick?.(row.original)}
          >
            {expenseNumber(row.original)}
          </button>
          {count > 1 ? (
            <Tooltip title={t('common:expense.viewBatch')}>
              <Chip
                size="small"
                variant="outlined"
                icon={<Layers sx={{ fontSize: 14 }} />}
                label={count}
                onClick={(event) => {
                  event.stopPropagation();
                  options?.onBatchClick?.(row.original);
                }}
                sx={{ height: 22, '& .MuiChip-label': { px: 0.6, fontWeight: 600 } }}
              />
            </Tooltip>
          ) : null}
        </Box>
      );
    },
  },
  {
    header: t('common:expense.payee'),
    accessorKey: 'payee',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
  },
  {
    header: t('common:expense.category'),
    accessorKey: 'category',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
    Cell: ({ cell }) => expenseCategoryLabel(String(cell.getValue() ?? ''), t),
  },
  {
    header: t('common:tables.amount'),
    accessorKey: 'amount',
    Cell: ({ cell }) => Util.formatNumber(Number(cell.getValue() || 0)),
    filterFn: 'greaterThanOrEqualTo',
    columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
  },
  {
    header: t('common:tables.mode'),
    accessorKey: 'mode_name',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
  },
  {
    header: t('common:tables.depot'),
    accessorKey: 'depot_name',
    Cell: ({ cell }) => String(cell.getValue() ?? '') || '—',
  },
  {
    header: t('common:tables.batch'),
    accessorKey: 'batch',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      return value ? `${value.slice(0, 8)}…` : '—';
    },
  },
  {
    header: t('common:tables.user'),
    accessorKey: 'user',
  },
  {
    header: t('common:wallet.description'),
    accessorKey: 'description',
    Cell: ({ cell }) => String(cell.getValue() ?? '') || '—',
  },
];
